// src/components/layouts/SideBar.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Shared Admin + Teacher Panel Sidebar.
//
//   • undefined / not passed  → Admin mode: show all modules, show Control Panel
//   • "all"                   → Teacher mode, no restrictions: show all modules
//   • Set<form_id>            → Teacher mode: show only forms in this Set
//
// The prop is applied AFTER fetchModules completes so there is no timing race.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useMemo } from 'react';
import { Security as SecurityIcon } from '@mui/icons-material';
import {
  AppBar, Toolbar, IconButton, Typography, Drawer, List,
  TextField, InputAdornment, ListItem, Button, ListItemIcon,
  ListItemText, Collapse, Box, Avatar, Menu, MenuItem, Divider,
  Badge, Dialog, DialogActions, DialogTitle, DialogContent,
  styled, ThemeProvider, GlobalStyles, createTheme, Tooltip, useMediaQuery,
  FormControl, Select, InputLabel, useTheme,
} from '@mui/material';
import { Chip } from '@mui/material';
import {
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  Notifications as NotificationsIcon,
  Dashboard as DashboardIcon,
  School as MasterIcon,
  Settings as CPanelIcon,
  Book as CourseIcon,
  Help as EnquiryIcon,
  AssignmentTurnedIn as AdmissionIcon,
  Payment as PaymentIcon,
  CheckCircle as AttendanceIcon,
  Assignment as AssignmentIcon,
  Description as CertificateIcon,
  Message as SmsIcon,
  Email as EmailIcon,
  People as CounsellorIcon,
  MonetizationOn as ExpenseIcon,
  AccountBalance as AccountingMasterIcon,
  Report as MisReportIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  AccountCircle as AccountIcon,
  Lock as LockIcon,
  ExitToApp as LogoutIcon,
  Close as CloseIcon,

  // Student information
  Phone as PhoneIcon,
  Class as ClassIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PayrollIcon from '@mui/icons-material/AccountBalanceWallet';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { lighten } from '@mui/material/styles';
import { tableScrollbarStyles } from './tableScrollbarStyles';
import { searchFieldOutlinedInputStyles } from '../ui/searchFieldStyles';
import ViewSidebarIcon from "@mui/icons-material/Sort";
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsSidebar from './SettingsSidebar';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import BadgeIcon from '@mui/icons-material/Badge';
import { usePdfSettings } from './PdfSettingsContext.js';
import axios from 'axios';
import PermissionPage from '../Permissionss/PermissionPage.js';
import { BranchProvider, useBranch } from './BranchContext';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import LocalLibraryRoundedIcon from '@mui/icons-material/LocalLibraryRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ── Client info (read fresh each render so teacher login is reflected) ──
const getClientInfo = () => {
  try {
    const raw = localStorage.getItem('client');
    if (raw && raw !== 'undefined') return JSON.parse(raw);
  } catch (err) {
    console.warn('Invalid client data:', err);
    localStorage.removeItem('client');
  }
  return null;
};

const CustomScrollbar = styled('div')({
  '&::-webkit-scrollbar': { width: '6px', background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { backgroundColor: 'transparent', borderRadius: '3px' },
  '&:hover::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)' },
  '&:hover::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(0,0,0,0.3)' },
  scrollbarWidth: 'thin',
  scrollbarColor: 'transparent transparent',
  '&:hover': { scrollbarColor: 'rgba(0,0,0,0.2) transparent' },
});
const iconMap = {
  master: <MasterIcon />,
  staff: <BadgeRoundedIcon />,
  course: <AutoStoriesRoundedIcon />,
  student: <PersonSearchRoundedIcon />,
  fee: <PaymentIcon />,
  assignment: <AssignmentIcon />,
  certificate: <CertificateIcon />,
  report: <QueryStatsRoundedIcon />,
  leave: <EventAvailableRoundedIcon />,
  'leave apply': <EventAvailableRoundedIcon />,
  'student details': <PersonSearchRoundedIcon />,
  'staff details': <BadgeRoundedIcon />,
  library: <LocalLibraryRoundedIcon />,
  'hr report': <AssessmentRoundedIcon />,
  'hr management': <Groups2RoundedIcon />,
  hr_management: <Groups2RoundedIcon />,

  City: <MasterIcon />,
  State: <MasterIcon />,
  Country: <MasterIcon />,
  Department: <MasterIcon />,
  Designation: <MasterIcon />,
  'Follow Up Medium': <MasterIcon />,
  'Interest Level': <MasterIcon />,
  Organization: <MasterIcon />,
  Branch: <MasterIcon />,
  Source: <MasterIcon />,
  Status: <MasterIcon />,
  'Payment Mode': <MasterIcon />,
  Nationality: <MasterIcon />,
  'Form Design': <MasterIcon />,

  'Employee Attendance': <CPanelIcon />,
  Employee: <CPanelIcon />,
  User: <CPanelIcon />,
  'User Group': <CPanelIcon />,

  Course: <AutoStoriesRoundedIcon />,
  Batch: <AutoStoriesRoundedIcon />,
  Module: <AutoStoriesRoundedIcon />,
  Topic: <AutoStoriesRoundedIcon />,
  'Course Tracker': <AutoStoriesRoundedIcon />,

  Enquiry: <EnquiryIcon />,
  'Enquiry Follow Up': <EnquiryIcon />,

  Registration: <AdmissionIcon />,
  Admission: <AdmissionIcon />,

  'Fee Generation': <PaymentIcon />,
  'Fee Deposit': <PaymentIcon />,
  'Due List': <PaymentIcon />,
  Attendance: <AttendanceIcon />,

  'Create Assignment': <AssignmentIcon />,
  'View Assignment': <AssignmentIcon />,

  'Certificate Approval': <CertificateIcon />,
  'Certificate Issue': <CertificateIcon />,
  Certificate: <CertificateIcon />,

  'Main Category': <InventoryIcon />,
  'Sub Category': <InventoryIcon />,
  Brand: <InventoryIcon />,
  Characteristics: <InventoryIcon />,
  'Product Master': <InventoryIcon />,

  'Salary Head Type': <PayrollIcon />,
  'Salary Head': <PayrollIcon />,

  // Different professional icons
  Leave: <EventAvailableRoundedIcon />,
  'Leave Apply': <EventAvailableRoundedIcon />,

  'Student Details': <PersonSearchRoundedIcon />,

  Staff: <BadgeRoundedIcon />,
  'Staff Details': <BadgeRoundedIcon />,

  Library: <LocalLibraryRoundedIcon />,

  'Admission Report': <QueryStatsRoundedIcon />,
  'Payment Report': <QueryStatsRoundedIcon />,
  'Attendance Report': <QueryStatsRoundedIcon />,
  'Enquiry Report': <QueryStatsRoundedIcon />,
  'Certificate Report': <QueryStatsRoundedIcon />,
  'Course Tracker Report': <QueryStatsRoundedIcon />,

  'HR Report': <AssessmentRoundedIcon />,
  'HR Management': <Groups2RoundedIcon />,
  'Hr Management': <Groups2RoundedIcon />,
  'hr_management': <Groups2RoundedIcon />,

  'Send SMS': <SmsIcon />,
  'SMS History': <SmsIcon />,
  'Send Email': <EmailIcon />,
  'SMS Template': <SmsIcon />,

  Requisition: <ShoppingCartIcon />,

  Category: <ExpenseIcon />,
  'Expense Details': <ExpenseIcon />,

  Customer: <AccountingMasterIcon />,
  Vendor: <AccountingMasterIcon />,
  'Expense Group': <AccountingMasterIcon />,
  'Expense Item': <AccountingMasterIcon />,
  'Other Income Group': <AccountingMasterIcon />,
  'Other Income Item': <AccountingMasterIcon />,

  Master: <AdminPanelSettingsRoundedIcon />,
  'Fee Management': <PaymentIcon />,
  Assignment: <AssignmentIcon />,
  Inventory: <InventoryIcon />,
  'Staff Performance': <CounsellorIcon />,
  Payroll: <PayrollIcon />,
  Report: <QueryStatsRoundedIcon />,
  Settings: <SmsIcon />,
  Purchase: <ShoppingCartIcon />,
  Expense: <CurrencyExchangeIcon />,
  'Accounting Master': <AccountingMasterIcon />,
};

const toTitleCase = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.trim().split(' ').filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};

const safeParseJSON = (str, fallback = {}) => {
  try { return JSON.parse(str) || fallback; }
  catch { return fallback; }
};


const BranchDropdown = () => {
  const { selectedBranchId, setSelectedBranchId, branchList, isMainBranchAdmin } = useBranch();
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  if (!branchList.length) return null; // nothing to show yet

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: 180,
        mr: 1,
        '& .MuiOutlinedInput-root': {
          borderRadius: '30px', // rounded corners
          // Always show the theme-colored border, not just on focus
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor,
            borderWidth: '2px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryColor,
            borderWidth: '2px',
          },
        },
        '& .MuiInputLabel-root': {
          color: primaryColor,
          '&.Mui-focused': {
            color: primaryColor,
          },
        },
      }}
    >
      <InputLabel id="navbar-branch-label">Branch</InputLabel>
      <Select
        labelId="navbar-branch-label"
        value={selectedBranchId}
        label="Branch"
        onChange={(e) => setSelectedBranchId(e.target.value)}
        disabled={!isMainBranchAdmin}
        sx={{
          borderRadius: '30px',
          color: theme.palette.text.primary,
          fontWeight: 500,
        }}
      >
        {branchList.map((branch) => (
          <MenuItem key={branch.id} value={String(branch.id)}>
            {branch.name || branch.branch_name || `Branch ${branch.id}`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SideBar Component

//   undefined → Admin (show all + Control Panel)
//   "all"     → Teacher with no restrictions
//   Set       → Teacher with specific allowed form_ids
// ─────────────────────────────────────────────────────────────────────────────
const SideBar = ({ children }) => {
  const {
    admissionPrefix,
    updatePdfThemeColor,
    updatePdfTextColor,
    updateAdmissionPrefix,
  } = usePdfSettings();

 

  const clientInfo = getClientInfo();
  const clientName = clientInfo?.name || 'Warals';
  const clientFirstLetter = clientName.charAt(0).toUpperCase();

  // ── Dashboard route differs between teacher and admin ──
  const dashboardRoute =  '/dashboard';

  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [rawModules, setRawModules] = useState([]); // unfiltered from API
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [primaryColor, setPrimaryColor] = useState(() => localStorage.getItem('primaryColor') || '#065793');
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const [layoutType, setLayoutType] = useState(() => localStorage.getItem('layoutType') || 'full');
  const [pdfThemeColor, setPdfThemeColor] = useState(() => localStorage.getItem('pdfThemeColor') || '#16355B');
  const [pdfTextColor, setPdfTextColor] = useState(() => localStorage.getItem('pdfTextColor') || '#000000');
  const [tempColor, setTempColor] = useState(primaryColor);
  const [permissionPageOpen, setPermissionPageOpen] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isSmallScreen = useMediaQuery('(max-width:900px)');
  const currentUser = safeParseJSON(
  sessionStorage.getItem("user")
);

const isAdmin = currentUser?.is_admin === true;
const userRole = currentUser?.role?.toLowerCase();
const [allowedFormIds, setAllowedFormIds] = useState(new Set());
console.log("========== USER PERMISSION DEBUG ==========");
console.log("Current User:", currentUser);
console.log("Role:", currentUser?.role);
console.log("Top-level role_id:", currentUser?.role_id);
console.log("Employee role_id:", currentUser?.employee_details?.role_id);
console.log("Admission details:", currentUser?.admission_details);
console.log("Admission role_id:", currentUser?.admission_details?.role_id);
useEffect(() => {
  console.log("🟢 SIDEBAR MOUNT");

  return () => {
    console.log("🔴 SIDEBAR UNMOUNT");
  };
}, []);

useEffect(() => {
  const fetchPermissions = async () => {
    console.log("========== PERMISSION FETCH START ==========");

    try {
      const user = safeParseJSON(
        sessionStorage.getItem("user")
      );

      // Determine role_id from user object
const roleId =
  user?.role_id ??
  user?.employee_details?.role_id;

      console.log("Permission Role ID:", roleId);

      if (!roleId) {
        console.warn("⚠️ No role_id found");
        return;
      }

      const accessToken = localStorage.getItem("accessToken");

      console.log("Access Token Exists:", !!accessToken);

      const url =
        `${API_BASE_URL}/api/elearning_settings/module-form-permissions/?role_id=${roleId}`;

      console.log("Permission API URL:", url);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      console.log("========== PERMISSION API RESPONSE ==========");
      console.log("Full Response:", response.data);
      console.log("Response Length:", response.data?.length);

      const roleData = response.data.find(
        (item) =>
          Number(item.role_id) === Number(roleId)
      );

      console.log("Matched Role Data:", roleData);

      const permissions = roleData?.permissions || [];

      console.log(
        "Permissions Count:",
        permissions.length
      );

      console.table(permissions);

      const allowed = new Set(
        permissions
          .filter((p) => p.can_read)
          .map((p) => Number(p.form_id))
      );

      console.log(
        "✅ Allowed Form IDs:",
        [...allowed]
      );

      setAllowedFormIds(allowed);

      console.log(
        "Permission state update requested"
      );
    } catch (error) {
      console.error(
        "❌ Permission API FAILED:",
        error
      );
    }

    console.log("========== PERMISSION FETCH END ==========");
  };

  fetchPermissions();
}, [API_BASE_URL]);
  // ── Apply permission filter to raw modules ──
const modules = useMemo(() => {
  // 1. is_admin has highest priority
  if (isAdmin) {
    console.log("👑 is_admin=true → ADMIN PANEL → SHOW ALL MODULES");
    return rawModules;
  }

  // 2. Non-admin → role based permissions
  console.log("👤 Non-admin → role:", userRole);

  if (!userRole) {
    console.log("⚠️ No role found → SHOW NO MODULES");
    return [];
  }

  // 3. Role-based permission filtering
  const filteredModules = rawModules
    .map((module) => {
      const filteredForms = (module.forms || []).filter((form) =>
        allowedFormIds.has(Number(form.form_id))
      );

      return {
        ...module,
        forms: filteredForms,
      };
    })
    .filter((module) => module.forms.length > 0);

  return filteredModules;
}, [rawModules, allowedFormIds, isAdmin, userRole]);
  // ── Auto-open menus on search ──
  useEffect(() => {
    if (searchTerm.trim() === '') { setOpenMenus({}); return; }
    const newOpenMenus = {};
    modules.forEach((module) => {
      const match = module.forms?.some((f) =>
        f.form_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (match) newOpenMenus[module.module_name] = true;
    });
    setOpenMenus(newOpenMenus);
  }, [searchTerm, modules]);

  // ── Collapse on small screens ──
  useEffect(() => {
    setSidebarCollapsed(isSmallScreen);
  }, [isSmallScreen]);

  // ── Fetch modules from API ──
 const fetchModules = async () => {
  console.log("========== MODULE FETCH START ==========");

  try {
    const accessToken =
      localStorage.getItem("accessToken");

    console.log(
      "Module API Token Exists:",
      !!accessToken
    );

    const response = await axios.get(
      `${API_BASE_URL}/api/elearning_settings/client/modules/`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    console.log(
      "========== MODULE API RESPONSE =========="
    );

    console.log(
      "Raw Module Response:",
      response.data
    );

    console.log(
      "Module Count:",
      response.data?.length
    );

    setRawModules(response.data);
  } catch (error) {
    console.error(
      "❌ Module API FAILED:",
      error
    );
  }

  console.log("========== MODULE FETCH END ==========");
};

  useEffect(() => { fetchModules(); }, []);

  const handleSaveSettings = () => {
    setSnackbarMessage('Settings saved successfully!');
    setSnackbarSeverity('success');
    setOpenSnackbar(true);
  };

  const handlePdfThemeColorChange = (color) => {
    setPdfThemeColor(color);
    localStorage.setItem('pdfThemeColor', color);
  };

  const handlePdfTextColorChange = (color) => {
    setPdfTextColor(color);
    localStorage.setItem('pdfTextColor', color);
  };

  useEffect(() => {
    document.body.style.backgroundColor = darkMode ? 'rgb(21, 38, 58)' : '#f0f5f9';
    document.body.style.color = darkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.8)';
  }, [darkMode]);

  const drawerWidth = (sidebarCollapsed && !isSidebarHovered) ? '100px' : '350px';
  const lightPrimaryColor = useMemo(() => lighten(primaryColor, 0.9), [primaryColor]);

  const theme = useMemo(() => createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: { main: primaryColor },
      background: {
        default: darkMode ? 'rgb(17, 28, 45)' : '#f0f5f9',
        paper: darkMode ? 'rgb(17, 28, 45)' : 'white',
      },
      text: {
        primary: darkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.8)',
        disabled: darkMode ? '#AAB4C3' : '#64748B',
      },
    },
    typography: { fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: 14 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&&[data-app-cancel="true"]': {
              color:
                theme.palette.mode === 'dark'
                  ? '#111827'
                  : theme.palette.primary.contrastText,
              backgroundColor:
                theme.palette.mode === 'dark'
                  ? '#fff'
                  : theme.palette.primary.main,
              border: `1px solid ${
                theme.palette.mode === 'dark'
                  ? '#fff'
                  : theme.palette.primary.main
              }`,
              '&:hover': {
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[200]
                    : theme.palette.primary.dark,
                borderColor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.grey[200]
                    : theme.palette.primary.dark,
              },
              '&.Mui-disabled': {
                opacity: 0.6,
              },
            },
          }),
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: ({ theme }) => tableScrollbarStyles(theme),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => searchFieldOutlinedInputStyles(theme),
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.primary,
            '&.Mui-focused': {
              color: theme.palette.primary.main,
            },
            '&.Mui-error': {
              color: theme.palette.error.main,
            },
            '&.Mui-disabled': {
              color: theme.palette.text.disabled,
            },
          }),
        },
      },
    },
  }), [darkMode, primaryColor]);

  const handleToggleLayoutType = (type) => {
    setLayoutType(type);
    localStorage.setItem('layoutType', type);
    setSidebarCollapsed(type === 'collapse');
  };

  const toggleSettings = () => setSettingsOpen(!settingsOpen);

  const handleDarkModeChange = (mode) => {
    setDarkMode(mode);
    localStorage.setItem('darkMode', JSON.stringify(mode));
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const handleMenuToggle = (menu) => {
    setOpenMenus((prev) => ({ ...prev, [menu]: !prev[menu] }));
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      const accessToken = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE_URL}/api/core/logout/`,
        { refresh: refreshToken },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('client');
      sessionStorage.removeItem('user');
      navigate('/');
    }
    handleMenuClose();
  };

  const handleOpenPermissions = () => setPermissionPageOpen(true);
  const handleClosePermissions = () => setPermissionPageOpen(false);

  const handleColorChange = (color) => setTempColor(color.hex);
  const handleColorConfirm = () => { setPrimaryColor(tempColor); };
  const handleColorCancel = () => setTempColor(primaryColor);

  const isMenuItemActive = (item) => {
    if (item.forms) return item.forms.some((f) => location.pathname === f.route);
    return location.pathname === item.route;
  };

  const isSubMenuItemActive = (form) => location.pathname === form.route;

  // Dashboard active: matches /dashboard
  const isDashboardActive =
    location.pathname === '/dashboard';

  const STATIC_ROUTES = [
    { pattern: '/dashboard', name: 'Dashboard', icon: <DashboardIcon /> },
    
  ];

  const matchStaticRoute = (pathname) => {
    const lowerPath = pathname.toLowerCase();
    for (const route of STATIC_ROUTES) {
      const regex = new RegExp(`^${route.pattern.toLowerCase().replace(/:[^/]+/g, '[^/]+')}$`);
      if (regex.test(lowerPath)) return route;
    }
    return null;
  };

  const getPageName = (pathname) => {
    const match = matchStaticRoute(pathname);
    if (match) return match.name;
    for (const module of modules) {
      for (const form of module.forms) {
        if (form.route === pathname) return toTitleCase(form.form_name);
      }
    }
    const segment = pathname.split('/').filter(Boolean)[0] || '';
    return toTitleCase(segment.replace(/-/g, ' '));
  };

  const getPageIcon = (pathname) => {
    const match = matchStaticRoute(pathname);
    if (match) return match.icon;
    for (const module of modules) {
      for (const form of module.forms) {
        if (form.route === pathname) return iconMap[form.icon] || <DashboardIcon />;
      }
    }
    return <DashboardIcon />;
  };

  const currentPageName = getPageName(location.pathname);
  const currentPageIcon = getPageIcon(location.pathname);

  // ── Sidebar drawer content ──
  const drawer = (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', height: '100%',
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        boxShadow: '0 0 20px rgba(0,0,0,0.1)',
        borderRadius: '16px', overflow: 'hidden', margin: '20px',
      }}
      onMouseEnter={() => setIsSidebarHovered(true)}
      onMouseLeave={() => setIsSidebarHovered(false)}
    >
      {/* Search */}
      <Box sx={{ flexShrink: 0, px: 2, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          fullWidth size="small" placeholder="Search here..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ display: (sidebarCollapsed && !isSidebarHovered) ? 'none' : 'block' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: theme.palette.text.primary, display: (sidebarCollapsed && !isSidebarHovered) ? 'none' : 'block' }} />
              </InputAdornment>
            ),
            sx: {
              backgroundColor: 'transparent', borderRadius: '8px',
              color: theme.palette.text.primary,
              border: `1px solid ${theme.palette.divider}`,
              '& .MuiInputBase-input::placeholder': { color: theme.palette.text.primary, opacity: 1 },
              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            },
          }}
        />
      </Box>

      <CustomScrollbar sx={{ flexGrow: 1, overflowY: 'auto', px: 1 }}>
        <List sx={{ py: 0 }}>
          {/* Dashboard link */}
          <ListItem
            button component={Link} to={dashboardRoute}
            sx={{
              cursor: 'pointer',
              color: isDashboardActive ? primaryColor : theme.palette.text.primary,
              px: 2, py: 1,
              backgroundColor: isDashboardActive ? lightPrimaryColor : 'transparent',
              borderRadius: '8px', margin: '4px 0',
              '&:hover': {
                backgroundColor: isDashboardActive ? lightPrimaryColor : theme.palette.action.hover,
                color: isDashboardActive ? primaryColor : theme.palette.text.primary,
              },
              transition: 'all 0.2s ease',
            }}
          >
            <ListItemIcon sx={{ color: isDashboardActive ? primaryColor : theme.palette.text.primary, minWidth: '36px' }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText
              primary="Dashboard"
              sx={{
                display: (sidebarCollapsed && !isSidebarHovered) ? 'none' : 'block',
                '& .MuiTypography-root': { fontSize: '1.1rem', fontWeight: isDashboardActive ? '600' : '400' },
              }}
            />
          </ListItem>

          {/* Dynamic modules (permission-filtered for teachers) */}
          {modules
            .filter((module) =>
              module.module_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              module.forms.some((f) => f.form_name.toLowerCase().includes(searchTerm.toLowerCase()))
            )
            .sort((a, b) => a.order - b.order)
            .map((module) => (
              <React.Fragment key={module.module_name}>
                <ListItem
                  button onClick={() => handleMenuToggle(module.module_name)}
                  sx={{
                    cursor: 'pointer',
                    color: isMenuItemActive(module) ? primaryColor : theme.palette.text.primary,
                    px: 2, py: 1,
                    backgroundColor: isMenuItemActive(module) ? lightPrimaryColor : 'transparent',
                    borderRadius: '8px', margin: '4px 0',
                    '&:hover': {
                      backgroundColor: isMenuItemActive(module) ? lightPrimaryColor : theme.palette.action.hover,
                      color: isMenuItemActive(module) ? primaryColor : theme.palette.text.primary,
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{ color: isMenuItemActive(module) ? primaryColor : theme.palette.text.primary, minWidth: '36px' }}>
                    {iconMap[module.icon] || iconMap[String(module.module_name || '').trim()] || iconMap[String(module.module_name || '').trim().toLowerCase()] || <DashboardIcon />}
                  </ListItemIcon>
                  <ListItemText
                    primary={toTitleCase(module.module_name)}
                    sx={{
                      display: (sidebarCollapsed && !isSidebarHovered) ? 'none' : 'block',
                      '& .MuiTypography-root': { fontSize: '1.1rem', fontWeight: isMenuItemActive(module) ? '600' : '400' },
                    }}
                  />
                  {module.forms && (
                    openMenus[module.module_name]
                      ? <ExpandLess sx={{ color: isMenuItemActive(module) ? primaryColor : theme.palette.text.primary, display: (sidebarCollapsed && !isSidebarHovered) ? 'none' : 'block' }} />
                      : <ExpandMore sx={{ color: isMenuItemActive(module) ? primaryColor : theme.palette.text.primary, display: (sidebarCollapsed && !isSidebarHovered) ? 'none' : 'block' }} />
                  )}
                </ListItem>

                {module.forms && (
                  <Collapse in={openMenus[module.module_name]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {module.forms
                        .filter((f) => f.form_name.toLowerCase().includes(searchTerm.toLowerCase()))
                        .sort((a, b) => a.order - b.order)
                        .map((form) => (
                          <ListItem
                            button
                            key={form.form_name}
                            component={Link}
                            to={
                              form.form_name === 'Salary Structure'
                                ? '/hr/salary-structure'
                                : form.route
                            }
                            sx={{
                              display: (sidebarCollapsed && !isSidebarHovered) ? 'none' : 'block',
                              pl: 6, py: 1,
                              color: isSubMenuItemActive(form) ? primaryColor : theme.palette.text.primary,
                              border: isSubMenuItemActive(form) ? `2px solid ${lightPrimaryColor}` : 'none',
                              borderRadius: '8px', margin: '4px 0',
                              '&:hover': {
                                backgroundColor: isSubMenuItemActive(form) ? 'transparent' : theme.palette.action.hover,
                                color: isSubMenuItemActive(form) ? primaryColor : theme.palette.text.primary,
                              },
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <ListItemText
                              primary={<><span style={{ marginRight: '8px' }}>•</span>{toTitleCase(form.form_name)}</>}
                              sx={{ '& .MuiTypography-root': { fontSize: '1rem', fontWeight: isSubMenuItemActive(form) ? '500' : '400' } }}
                            />
                          </ListItem>
                        ))}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            ))}
        </List>
      </CustomScrollbar>

      {/* Control Panel — ADMIN ONLY (hidden for teacher mode) */}
      {isAdmin && (
        <List sx={{ mt: 'auto', p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <ListItem
            button onClick={handleOpenPermissions}
            sx={{
              cursor: 'pointer', color: theme.palette.text.primary,
              px: 2, py: 1.5, borderRadius: '8px', margin: '4px 0',
              '&:hover': { backgroundColor: theme.palette.action.hover },
              transition: 'all 0.2s ease',
            }}
          >
            <ListItemIcon sx={{ color: theme.palette.text.primary, minWidth: '36px' }}>
              <CPanelIcon />
            </ListItemIcon>
            <ListItemText
              primary="Control Panel"
              sx={{
                display: (sidebarCollapsed && !isSidebarHovered) ? 'none' : 'block',
                '& .MuiTypography-root': { fontSize: '1.1rem', fontWeight: '500' },
              }}
            />
          </ListItem>
        </List>
      )}
    </Box>
  );
console.log("========== FINAL SIDEBAR STATE ==========");

console.log({
  pathname: location.pathname,
  role: currentUser?.role,
  isAdmin,
  rawModules: rawModules.length,
  allowedFormIds: [...allowedFormIds],
  filteredModules: modules.length,
});

console.log(
  "Visible Forms:",
  modules.flatMap((module) =>
    (module.forms || []).map(
      (form) => ({
        module: module.module_name,
        formId: form.form_id,
        formName: form.form_name,
      })
    )
  )
);
  return (
    <BranchProvider>
    <ThemeProvider theme={theme}>
      <GlobalStyles
        styles={{
          ':root': {
            colorScheme: darkMode ? 'dark' : 'light',
          },
        }}
      />
      {/* ── AppBar ── */}
      <AppBar
        position="sticky"
        sx={{
          ml: { sm: drawerWidth },
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundColor: theme.palette.background.paper,
          height: '85px', borderRadius: '16px',
          margin: '20px', mb: 0,
          width: { sm: `calc(100% - ${drawerWidth} - 40px)` },
          justifyContent: 'space-between',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', borderRadius: '16px', height: '100%', px: 3, backgroundColor: theme.palette.background.paper }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                <ViewSidebarIcon sx={{ color: theme.palette.text.primary }} />
              </IconButton>
            </Box>
            <IconButton
              color="inherit" aria-label="open drawer" edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' }, color: '#333' }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h4" noWrap component="div"
              sx={{ color: theme.palette.text.primary, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 1, fontFamily: "'Alice', serif" }}
            >
              {React.cloneElement(currentPageIcon, { style: { color: primaryColor, fontSize: '2.2rem' } })}
              {currentPageName}
            </Typography>
          </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <BranchDropdown />
            <Tooltip title="Settings" arrow>
              <IconButton color="inherit" onClick={toggleSettings} sx={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', marginRight: '-25px' }}>
                <SettingsIcon sx={{ fontSize: 32 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'} arrow>
              <IconButton
                color="inherit" onClick={() => handleDarkModeChange(!darkMode)}
                sx={{ color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)', marginRight: '-25px' }}
              >
                {darkMode ? <LightModeIcon sx={{ fontSize: 32 }} /> : <DarkModeIcon sx={{ fontSize: 32 }} />}
              </IconButton>
            </Tooltip>


            
       {/* <Tooltip title="Notifications" arrow>
  <IconButton
    color="inherit"
    onClick={() => { }}
    sx={{
      color: darkMode
        ? 'rgba(255,255,255,0.7)'
        : 'rgba(0,0,0,0.7)',
    }}
  >
    <Badge badgeContent={4} color="error">
      <NotificationsIcon sx={{ fontSize: 32 }} />
    </Badge>
  </IconButton>
</Tooltip> */}


       <Box
  sx={{
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
    cursor: 'pointer',
    ml: 2,
  }}
  onClick={handleMenuOpen}
>
  {/* Avatar */}
  <Avatar
    sx={{
      width: 42,
      height: 42,
      bgcolor: primaryColor,
      fontSize: '1rem',
      fontWeight: 700,
      color: '#fff',
    }}
  >
    {(
      currentUser?.employee_name ||
      currentUser?.student_name ||
      currentUser?.candidate_name ||
      currentUser?.user_id ||
      'User'
    )
      .trim()
      .charAt(0)
      .toUpperCase()}
  </Avatar>

  {/* Name + Role */}
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      minWidth: 80,
    }}
  >
    <Typography
      sx={{
        fontWeight: 600,
        fontSize: '1rem',
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
        color:
          theme.palette.mode === 'dark'
            ? '#ffffff'
            : '#222222',
      }}
    >
      {currentUser?.employee_name ||
        currentUser?.student_name ||
        currentUser?.candidate_name ||
        currentUser?.user_id ||
        'User'}
    </Typography>

    <Typography
      sx={{
        fontSize: '0.72rem',
        fontWeight: 500,
        lineHeight: 1.2,
        mt: 0.2,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
        color:
          theme.palette.mode === 'dark'
            ? '#a0a0a0'
            : '#666666',
      }}
    >
      <Chip
  label={
    currentUser?.role
      ? currentUser.role.charAt(0).toUpperCase() +
        currentUser.role.slice(1)
      : 'User'
  }
  size="small"
  sx={{
    mt: 0.4,
    height: 22,
    fontSize: '0.68rem',
    fontWeight: 600,
    textTransform: 'capitalize',
    borderRadius: '6px',

    backgroundColor:
      theme.palette.mode === 'dark'
        ? `${primaryColor}25`
        : `${primaryColor}15`,

    color: primaryColor,

    '& .MuiChip-label': {
      px: 1,
    },
  }}
/>
    </Typography>
  </Box>
</Box>
            <Menu
              anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}
              sx={{ mt: 1 }}
              PaperProps={{
                elevation: 1,
                sx: { borderRadius: '8px', minWidth: '200px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', '& .MuiMenuItem-root': { fontSize: '0.875rem', padding: '8px 16px' } },
              }}
            >
              <MenuItem onClick={() => { handleMenuClose(); setAccountModalOpen(true); }}>
                <AccountIcon sx={{ mr: 1, fontSize: '1.1rem', color: (t) => t.palette.text.primary }} />
                Account
              </MenuItem>
              <MenuItem onClick={handleMenuClose}>
                <LockIcon sx={{ mr: 1, fontSize: '1.1rem', color: (t) => t.palette.text.primary }} />
                Change Password
              </MenuItem>
              <Divider sx={{ my: '4px !important' }} />
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1, fontSize: '1.1rem', color: (t) => t.palette.text.primary }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* ── Nav Drawer ── */}
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }} aria-label="sidebar">
        <Drawer
          variant="temporary" open={mobileOpen} onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none', background: 'transparent' } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{ display: { xs: 'none', sm: 'block' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth, borderRight: 'none', background: 'transparent', transition: 'width 0.3s ease' } }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* ── Main content ── */}
      <Box
        component="main"
        sx={{
          flexGrow: 1, p: 3, pl: 0, pr: 0, mr: 0,
          width: { sm: `calc(100% - ${drawerWidth} - 23px)` },
          backgroundColor: 'transparent', minHeight: '100vh',
          marginLeft: { sm: drawerWidth }, overflow: 'hidden',
        }}
      >
        {children}
      </Box>

      {/* ── Settings Sidebar ── */}
      <SettingsSidebar
        open={settingsOpen} onClose={() => setSettingsOpen(false)}
        darkMode={darkMode} onToggleDarkMode={handleDarkModeChange}
        primaryColor={primaryColor}
        onChangePrimaryColor={(color) => { setPrimaryColor(color); localStorage.setItem('primaryColor', color); }}
        layoutType={layoutType} onToggleLayoutType={handleToggleLayoutType}
        pdfThemeColor={pdfThemeColor} onChangePdfThemeColor={handlePdfThemeColorChange}
        pdfTextColor={pdfTextColor} onChangePdfTextColor={handlePdfTextColorChange}
        onSaveSettings={handleSaveSettings}
        admissionPrefix={admissionPrefix} onChangeAdmissionPrefix={updateAdmissionPrefix}
      />

{/* ── Permission Dialog — ADMIN ONLY ── */}
{isAdmin && (
  <Dialog
    open={permissionPageOpen}
    onClose={handleClosePermissions}
    maxWidth={false}
    fullWidth
    PaperProps={{
      sx: {
        width: 'calc(100% - 32px)',
        maxWidth: '1600px',
        height: { xs: '95dvh', md: '90vh' },
        maxHeight: { xs: '95dvh', md: '90vh' },
        m: { xs: 1, sm: 2 },
        borderRadius: { xs: '12px', sm: '18px' },
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: 1,
        borderColor: 'divider',
        backgroundImage: 'none',
        boxShadow: (theme) =>
          theme.palette.mode === 'dark'
            ? '0 24px 80px rgba(0, 0, 0, 0.45)'
            : '0 24px 80px rgba(15, 23, 42, 0.16)',
      },
    }}
  >
    {/* Dialog Header */}
    <DialogTitle
      sx={{
        p: { xs: 2, sm: 2.5, md: 3 },
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.25, sm: 2 },
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: { xs: 42, sm: 48 },
              height: { xs: 42, sm: 48 },
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              bgcolor: 'action.hover',
              color: 'primary.main',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <SecurityIcon sx={{ fontSize: { xs: 23, sm: 27 } }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.05rem', sm: '1.35rem' },
                fontWeight: 700,
                letterSpacing: '-0.35px',
                lineHeight: 1.4,
                color: 'text.primary',
              }}
            >
              Control Panel — Permissions
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.5,
                color: 'text.secondary',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
              }}
            >
              Manage roles, module access and system permissions.
            </Typography>
          </Box>
        </Box>

        {/* X Close Button */}
        <IconButton
          onClick={handleClosePermissions}
          aria-label="Close permissions dialog"
          size="small"
          sx={{
            flexShrink: 0,
            width: 38,
            height: 38,
            border: 1,
            borderColor: 'divider',
            borderRadius: '10px',
            color: 'text.secondary',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: 'action.hover',
              color: 'primary.main',
              borderColor: 'primary.main',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
    </DialogTitle>

    {/* Dialog Content */}
    <DialogContent
      sx={{
        p: 0,
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        bgcolor: 'background.default',
        '&:first-of-type': {
          pt: 0,
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          minHeight: '100%',
          boxSizing: 'border-box',
        }}
      >
        <PermissionPage />
      </Box>
    </DialogContent>

    {/* Dialog Footer — Information Only */}
    <DialogActions
      sx={{
        flexShrink: 0,
        justifyContent: 'flex-start',
        px: { xs: 2, sm: 3 },
        py: 1.5,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          display: { xs: 'none', sm: 'block' },
        }}
      >
        Review permission changes before saving.
      </Typography>
    </DialogActions>
  </Dialog>
)}

      {/* ── Account Modal ── */}
      <Dialog
        open={accountModalOpen} onClose={() => setAccountModalOpen(false)}
        maxWidth="sm" fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            background: darkMode ? 'linear-gradient(145deg, #1a2744 0%, #0f1c38 100%)' : 'linear-gradient(145deg, #ffffff 0%, #f0f5ff 100%)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.25)', overflow: 'hidden', maxHeight: '80vh',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)`, px: 4, py: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.25)', fontSize: '1.5rem', fontWeight: 700, color: '#fff', border: '2px solid rgba(255,255,255,0.4)' }}>
              {clientFirstLetter}
            </Avatar>
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.2 }}>
                {currentUser.user_id || clientName}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem', mt: 0.3 }}>
                {currentUser.is_admin ? 'Administrator' : currentUser.role || 'User'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setAccountModalOpen(false)} sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.15)' } }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <CustomScrollbar sx={{ maxHeight: 'calc(80vh - 150px)', overflowY: 'auto' }}>
          <DialogContent sx={{ px: 4, py: 3 }}>
  {(() => {
    const InfoRow = ({ icon, label, value }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 1.5,
          borderBottom: `1px solid ${
            darkMode
              ? 'rgba(255,255,255,0.07)'
              : 'rgba(0,0,0,0.06)'
          }`,
          '&:last-child': {
            borderBottom: 'none',
          },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            background: `${primaryColor}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, {
            sx: {
              fontSize: 18,
              color: primaryColor,
            },
          })}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '0.72rem',
              color: darkMode
                ? 'rgba(255,255,255,0.4)'
                : 'rgba(0,0,0,0.4)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            {label}
          </Typography>

          <Typography
            sx={{
              fontSize: '0.92rem',
              color: theme.palette.text.primary,
              fontWeight: 500,
              mt: 0.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value || '—'}
          </Typography>
        </Box>
      </Box>
    );

    const isStudent = currentUser?.role === 'student';

    // ================= STUDENT =================
    if (isStudent) {
      const student = currentUser?.admission_details;

      return (
        <Box>
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: primaryColor,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 1.5,
            }}
          >
            Student Information
          </Typography>

          <Box
            sx={{
              background: darkMode
                ? 'rgba(255,255,255,0.04)'
                : 'rgba(0,0,0,0.02)',
              borderRadius: '14px',
              px: 2,
              mb: 3,
            }}
          >
           <InfoRow
  icon={<PersonIcon />}
  label="Student Name"
  value={student?.student_name}
/>

<InfoRow
  icon={<AdmissionIcon />}
  label="Admission No"
  value={student?.admission_no}
/>

<InfoRow
  icon={<EmailIcon />}
  label="Email"
  value={student?.email}
/>

<InfoRow
  icon={<PhoneIcon />}
  label="Mobile No"
  value={student?.mobile_no}
/>

<InfoRow
  icon={<AccountIcon />}
  label="Branch"
  value={student?.branch_name}
/>
          </Box>

          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: primaryColor,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: 1.5,
            }}
          >
            Course Information
          </Typography>

          <Box
            sx={{
              background: darkMode
                ? 'rgba(255,255,255,0.04)'
                : 'rgba(0,0,0,0.02)',
              borderRadius: '14px',
              px: 2,
            }}
          >
            {currentUser?.courses?.length > 0 ? (
              currentUser.courses.map((course, index) => (
                <Box key={course.course_id || index}>
               <InfoRow
  icon={<CourseIcon />}
  label="Course"
  value={course.course_name}
/>

<InfoRow
  icon={<ClassIcon />}
  label="Batch"
  value={course.batch_name}
/>
                </Box>
              ))
            ) : (
    <Typography
      sx={{
        py: 2,
        color: theme.palette.text.disabled,
        fontSize: '0.9rem',
      }}
    >
      No course assigned
    </Typography>
  )}
          </Box>
        </Box>
      );
    }

    // ================= ADMIN / TEACHER / EXISTING =================
    return (
      <Box>
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 700,
            color: primaryColor,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 1.5,
          }}
        >
          Client Information
        </Typography>

        <Box
          sx={{
            background: darkMode
              ? 'rgba(255,255,255,0.04)'
              : 'rgba(0,0,0,0.02)',
            borderRadius: '14px',
            px: 2,
            mb: 3,
          }}
        >
          <InfoRow
            icon={<PersonIcon />}
            label="Client Name"
            value={clientInfo?.name}
          />

          <InfoRow
            icon={<EmailIcon />}
            label="Email"
            value={clientInfo?.email}
          />

          <InfoRow
            icon={<BadgeIcon />}
            label="Client Code"
            value={clientInfo?.code}
          />

          <InfoRow
            icon={<AccountIcon />}
            label="Account Expiry"
            value={clientInfo?.expiry_date}
          />
        </Box>

        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 700,
            color: primaryColor,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 1.5,
          }}
        >
          Session Information
        </Typography>

        <Box
          sx={{
            background: darkMode
              ? 'rgba(255,255,255,0.04)'
              : 'rgba(0,0,0,0.02)',
            borderRadius: '14px',
            px: 2,
          }}
        >
          <InfoRow
            icon={<LockIcon />}
            label="Role"
            value={
              currentUser?.is_admin
                ? 'Administrator'
                : currentUser?.role || 'User'
            }
          />

          <InfoRow
            icon={<AccountIcon />}
            label="Account Status"
            value={
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: '50%',
                    bgcolor: '#22c55e',
                    display: 'inline-block',
                  }}
                />
                Active
              </Box>
            }
          />
        </Box>
      </Box>
    );
  })()}
</DialogContent>
        </CustomScrollbar>

        <DialogActions sx={{ px: 4, pb: 3, pt: 0, gap: 1.5 }}>
          <Button fullWidth variant="outlined" onClick={() => setAccountModalOpen(false)}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)', color: theme.palette.text.primary }}>
            Close
          </Button>
          <Button fullWidth variant="contained" onClick={() => { setAccountModalOpen(false); handleLogout(); }}
            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`, boxShadow: `0 4px 14px ${primaryColor}55` }}>
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
    </BranchProvider>
  );
};

export default SideBar;